/**
 * Settings · Warehouse management (supervisor/admin). Full CRUD over the Vari
 * pickup-origin warehouses — the `locations` (kind='vari_warehouse') rows that
 * drive the trip/mileage calculator origins, the intake origin-warehouse picker,
 * and the AI "Shipping From" grounding. Reuses AddressAutocomplete so an address
 * brings its own lat/long (required for a trip origin). Delete is a soft-delete.
 */
import { useState } from 'react';
import { Loader2, MapPin, Pencil, Plus, Trash2, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import type { AddressSuggestion, Location, LocationCreateInput } from '@vari-crew/schemas';
import {
  useCreateLocation,
  useDeleteLocation,
  useLocations,
  useUpdateLocation,
} from '@/api/locations';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog, Dialog } from '@/components/ui/Dialog';
import { Input, Label, Textarea } from '@/components/ui/Input';

export function WarehousesCard() {
  const { data, isLoading, error } = useLocations('vari_warehouse');
  const del = useDeleteLocation();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [confirmDel, setConfirmDel] = useState<Location | null>(null);

  const warehouses = data?.data ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Warehouse className="w-4 h-4" /> Warehouses
          </CardTitle>
          <p className="text-xs text-ink-dim mt-1.5 max-w-prose">
            Pickup-origin warehouses for the trip calculator, the intake origin picker, and the AI
            &ldquo;Shipping From&rdquo; match. The address auto-fills coordinates — needed for a
            warehouse to appear as a trip origin.
          </p>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => setAdding(true)}>
          <Plus className="w-3.5 h-3.5" /> Add warehouse
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-ink-faint" />
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error).message}
          </div>
        ) : warehouses.length === 0 ? (
          <div className="py-8 text-center text-sm text-ink-faint">
            No warehouses yet. Add one to get started.
          </div>
        ) : (
          <ul className="divide-y divide-line border-t border-line">
            {warehouses.map((w) => {
              const addr = [w.address.line1, w.address.city, w.address.state, w.address.postal_code]
                .filter(Boolean)
                .join(', ');
              return (
                <li key={w.id} className="flex items-start gap-3 px-5 py-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-ink-faint" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{w.name}</div>
                    <div className="text-xs text-ink-faint truncate">{addr || 'Address TBD'}</div>
                    {w.address.latitude == null && (
                      <div className="text-[11px] text-status-requested mt-0.5">
                        No coordinates — won&apos;t appear as a trip origin.
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(w)}
                      aria-label={`Edit ${w.name}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDel(w)}
                      aria-label={`Delete ${w.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-status-blocked-fg" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {(adding || editing) && (
        <WarehouseDialog
          warehouse={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDel != null}
        onClose={() => setConfirmDel(null)}
        busy={del.isPending}
        tone="danger"
        confirmLabel="Delete"
        title={`Delete ${confirmDel?.name ?? 'warehouse'}?`}
        body="It will be removed from the trip calculator, the intake origin picker, and the AI list. Projects that already list it as an origin keep their record."
        onConfirm={() => {
          if (!confirmDel) return;
          const name = confirmDel.name;
          del.mutate(confirmDel.id, {
            onSuccess: () => {
              toast.success(`${name} deleted`);
              setConfirmDel(null);
            },
            onError: (e) => toast.error(e.message),
          });
        }}
      />
    </Card>
  );
}

function WarehouseDialog({
  warehouse,
  onClose,
}: {
  warehouse: Location | null;
  onClose: () => void;
}) {
  const create = useCreateLocation();
  const update = useUpdateLocation();
  const editing = warehouse != null;
  const busy = create.isPending || update.isPending;

  const [name, setName] = useState(warehouse?.name ?? '');
  const [line1, setLine1] = useState(warehouse?.address.line1 ?? '');
  const [line2, setLine2] = useState(warehouse?.address.line2 ?? '');
  const [city, setCity] = useState(warehouse?.address.city ?? '');
  const [state, setState] = useState(warehouse?.address.state ?? '');
  const [postal, setPostal] = useState(warehouse?.address.postal_code ?? '');
  const [lat, setLat] = useState<number | null>(warehouse?.address.latitude ?? null);
  const [lng, setLng] = useState<number | null>(warehouse?.address.longitude ?? null);
  const [contactName, setContactName] = useState(warehouse?.contact_name ?? '');
  const [contactPhone, setContactPhone] = useState(warehouse?.contact_phone ?? '');
  const [notes, setNotes] = useState(warehouse?.notes ?? '');

  function applySuggestion(s: AddressSuggestion) {
    setLine1(s.line1);
    if (s.city) setCity(s.city);
    if (s.state) setState(s.state);
    if (s.postal_code) setPostal(s.postal_code);
    setLat(s.latitude ?? null);
    setLng(s.longitude ?? null);
  }

  function submit() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('A warehouse name is required.');
      return;
    }
    if (!line1.trim() || !city.trim() || state.trim().length !== 2 || !postal.trim()) {
      toast.error('A full address (street, city, 2-letter state, ZIP) is required.');
      return;
    }
    const body: LocationCreateInput = {
      kind: 'vari_warehouse',
      name: trimmedName,
      address: {
        line1: line1.trim(),
        line2: line2.trim() || null,
        city: city.trim(),
        state: state.trim().toUpperCase(),
        postal_code: postal.trim(),
        country: 'US',
        latitude: lat,
        longitude: lng,
      },
      contact_name: contactName.trim() || null,
      contact_phone: contactPhone.trim() || null,
      notes: notes.trim() || null,
    };
    const opts = {
      onSuccess: () => {
        toast.success(editing ? `${trimmedName} updated` : `${trimmedName} added`);
        onClose();
      },
      onError: (e: Error) => toast.error(e.message),
    };
    if (editing && warehouse) update.mutate({ id: warehouse.id, body }, opts);
    else create.mutate(body, opts);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      busy={busy}
      title={editing ? 'Edit warehouse' : 'Add warehouse'}
      className="max-w-lg"
    >
      <div className="p-4 space-y-3">
        <div>
          <Label htmlFor="wh-name">Name</Label>
          <Input
            id="wh-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Northlake Warehouse"
          />
        </div>
        <div>
          <Label htmlFor="wh-line1">Address line 1</Label>
          <AddressAutocomplete
            id="wh-line1"
            value={line1}
            onChange={setLine1}
            onSelect={applySuggestion}
            placeholder="Start typing a street address…"
          />
        </div>
        <div>
          <Label htmlFor="wh-line2">Address line 2</Label>
          <Input
            id="wh-line2"
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
            placeholder="Suite, dock, building (optional)"
          />
        </div>
        <div className="grid grid-cols-[1fr_5rem_7rem] gap-2">
          <div>
            <Label htmlFor="wh-city">City</Label>
            <Input id="wh-city" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="wh-state">State</Label>
            <Input
              id="wh-state"
              value={state}
              maxLength={2}
              onChange={(e) => setState(e.target.value)}
              placeholder="TX"
            />
          </div>
          <div>
            <Label htmlFor="wh-zip">ZIP</Label>
            <Input id="wh-zip" value={postal} onChange={(e) => setPostal(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="wh-contact">Contact name</Label>
            <Input
              id="wh-contact"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label htmlFor="wh-phone">Contact phone</Label>
            <Input
              id="wh-phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Optional — e.g. (555) 123-4567 ext. 12"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="wh-notes">Notes</Label>
          <Textarea
            id="wh-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Dock hours, 3PL, access notes…"
          />
        </div>
        <p className="text-[11px] text-ink-faint">
          Picking an address suggestion fills city/state/ZIP and the map coordinates. Renaming a
          warehouse won&apos;t rename it on projects that already list it as an origin.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {editing ? 'Save' : 'Add warehouse'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
